import { translator } from "../../translation/translator";
import commonUtil from "../../util/common-utils";
import { Roles } from "../../constants/roles";
import { UsersAdminUtils, type UsersAdminLoginHistoryItem } from "../../user/admin/users-admin-utils";
import { populateIpGeolocationTooltips } from "../ip-geolocation/ip-geolocation-tooltip";
import type { LoginHistoryForm } from "./login-history-form";

/** IP lookup link target for the login-history modal's IP column — null when there's no real IP to link. */
function ipLookupUrl(ip: string): string | null {
    const trimmed = ip.trim();
    return trimmed && trimmed !== "-" ? `https://ipinfo.io/${encodeURIComponent(trimmed)}` : null;
}

export class LoginHistoryRender {
    private readonly form: LoginHistoryForm;

    public constructor(form: LoginHistoryForm) {
        this.form = form;
    }

    public renderTitle(displayName: string): void {
        const titleEl = this.form.getTitleEl();
        if (!(titleEl instanceof HTMLElement)) return;

        titleEl.textContent = translator.translate("users_admin_login_history_title", {
            name: displayName || "-",
        });
    }

    public renderLoading(): void {
        this.setStatus(translator.translate("users_admin_login_history_loading"), true);

        const bodyEl = this.form.getBodyEl();
        if (bodyEl instanceof HTMLElement) bodyEl.innerHTML = "";
    }

    public renderError(): void {
        this.setStatus(translator.translate("users_admin_login_history_load_failed"), false);

        const bodyEl = this.form.getBodyEl();
        if (bodyEl instanceof HTMLElement) bodyEl.innerHTML = "";
    }

    public renderList(items: UsersAdminLoginHistoryItem[]): void {
        const statusEl = this.form.getStatusEl();
        const bodyEl = this.form.getBodyEl();
        const spinnerEl = this.form.getSpinnerEl();

        if (!(bodyEl instanceof HTMLElement)) return;

        if (!Array.isArray(items) || items.length === 0) {
            this.setStatus(translator.translate("users_admin_login_history_empty"), false);
            bodyEl.innerHTML = "";
            return;
        }

        if (statusEl instanceof HTMLElement) {
            statusEl.hidden = true;
            statusEl.textContent = "";
        }

        if (spinnerEl instanceof HTMLElement) {
            spinnerEl.hidden = true;
            spinnerEl.setAttribute("aria-hidden", "true");
        }

        bodyEl.innerHTML = items
            .map((item) => {
                const createdAt = commonUtil.escapeHtml(UsersAdminUtils.formatDateTime(item.created_at));
                const method = commonUtil.escapeHtml(UsersAdminUtils.toText(item.login_method) || "-");
                const device = commonUtil.escapeHtml(UsersAdminUtils.toText(item.device_name) || "-");
                const rawIp = UsersAdminUtils.toText(item.ip_address).trim();
                const ipAddress = commonUtil.escapeHtml(rawIp || "-");
                const ipLookup = ipLookupUrl(rawIp);
                const loadingText = commonUtil.escapeHtml(translator.translate("ip_geolocation_loading"));
                // The IP address itself is the link (ISS-09-009); hovering shows IP
                // Geolocation via the shared info-tooltip engine — nested source
                // span's text is populated async by populateIpGeolocationTooltips()
                // below, keyed off data-ip, after this HTML is in the DOM.
                const ipCell = ipLookup
                    ? `<a href="${commonUtil.escapeHtmlAttribute(ipLookup)}" target="_blank" rel="noopener noreferrer" class="login-history-modal__ip-link" data-info-tooltip-source="1">${ipAddress}<span data-role="${Roles.INFO_TOOLTIP_SOURCE}" data-ip="${commonUtil.escapeHtmlAttribute(rawIp)}" hidden>${loadingText}</span></a>`
                    : ipAddress;
                const failureReason = commonUtil.escapeHtml(UsersAdminUtils.loginFailureReasonLabel(item.failure_reason));
                const success = UsersAdminUtils.toBoolean(item.is_successful);
                const resultTranslateKey = success
                    ? "users_admin_login_history_success"
                    : "users_admin_login_history_failed";
                const resultLabel = commonUtil.escapeHtml(translator.translate(resultTranslateKey));
                const resultClass = success ? "is-success" : "is-failed";

                return `
                    <article class="login-history-modal__item">
                        <div class="login-history-modal__item-top">
                            <span class="login-history-modal__badge ${resultClass}" data-translate="${resultTranslateKey}">${resultLabel}</span>
                            <span class="login-history-modal__date">${createdAt}</span>
                        </div>
                        <dl class="login-history-modal__grid">
                            <div>
                                <dt data-translate="users_admin_login_history_method">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_method"))}</dt>
                                <dd>${method}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_device">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_device"))}</dt>
                                <dd>${device}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_ip">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_ip"))}</dt>
                                <dd>${ipCell}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_reason">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_reason"))}</dt>
                                <dd>${failureReason}</dd>
                            </div>
                        </dl>
                    </article>
                `;
            })
            .join("");

        populateIpGeolocationTooltips(bodyEl);
    }

    private setStatus(message: string, loading: boolean): void {
        const statusEl = this.form.getStatusEl();
        const spinnerEl = this.form.getSpinnerEl();

        if (statusEl instanceof HTMLElement) {
            statusEl.hidden = false;
            statusEl.textContent = message;
        }

        if (spinnerEl instanceof HTMLElement) {
            spinnerEl.hidden = !loading;
            spinnerEl.setAttribute("aria-hidden", loading ? "false" : "true");
        }
    }
}
